/**
 * SharePoint Page
 * Main page for browsing and syncing SharePoint Test Plan files
 */

import { useState } from 'react';
import FolderBrowser from '../components/FolderBrowser';
import SyncProgress from '../components/SyncProgress';
import { sharePointApi, SyncItem, SyncResult } from '../services/api';

export default function SharePoint() {
  const [selectedItems, setSelectedItems] = useState<SyncItem[]>([]);
  const [recursive, setRecursive] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    if (selectedItems.length === 0) return;

    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      const result = await sharePointApi.syncItems(selectedItems, { recursive });
      setSyncResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectionChange = (items: SyncItem[]) => {
    setSelectedItems(items);
    // Clear previous results when selection changes
    setSyncResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SharePoint Sync</h1>
          <p className="text-gray-600">
            Browse and sync Test Plan files from SharePoint
          </p>
        </div>
      </div>

      {/* Folder Browser */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium mb-4">Browse Test Plans</h2>
        <FolderBrowser onSelectionChange={handleSelectionChange} />
      </div>

      {/* Sync Options & Button */}
      {selectedItems.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={recursive}
                  onChange={(e) => setRecursive(e.target.checked)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">Include subfolders</span>
              </label>
              <span className="text-sm text-gray-500">
                {selectedItems.length} item
                {selectedItems.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <button
              onClick={handleSync}
              disabled={syncing || selectedItems.length === 0}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {syncing && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              )}
              {syncing ? 'Syncing...' : 'Sync Selected'}
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <ErrorIcon className="w-5 h-5 text-red-500" />
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Sync Progress/Results */}
      <SyncProgress result={syncResult} isLoading={syncing} />
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  );
}
